import React, { useState, useEffect } from "react";
import { Container, Typography, Box, Button, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { firestore } from "../firebase.config";
import { collection, doc, getDocs, updateDoc, setDoc, query, where } from "firebase/firestore";
import PageNavBar from "../components/PageNavBar"; 
import { format, addDays, differenceInCalendarDays } from 'date-fns';
import { useUserContext } from "../context/UserContext";
import TenantBanner from "../components/TenantBanner";

const ApproveLeave = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [showAllRequests, setShowAllRequests] = useState(false);
  const { userRole, tenantId, selectedTenantId } = useUserContext();


  useEffect(() => {
    const fetchLeaveRequests = async () => {
      const snapshot = await getDocs(collection(firestore, "leaves"));

      const filterTenantId =
        userRole === "SuperAdmin"
          ? selectedTenantId || sessionStorage.getItem("selectedTenantId")
          : tenantId || sessionStorage.getItem("selectedTenantId");

      if (!filterTenantId) {
        console.warn("Missing tenantId, cannot filter leave requests.");
        return;
      }

      // First, get all active users from that tenant
      const usersSnapshot = await getDocs(
        query(collection(firestore, "Users"), where("Is_Active", "==", true), where("tenantId", "==", filterTenantId))
      );

      const allowedEmails = usersSnapshot.docs.map(doc => doc.data().userEmail);

      // Include:
      // - leave requests that have tenantId and match
      // - OR don't have tenantID but the user is in the allowed email list
      const requests = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(req =>
          (req.tenantId && req.tenantId === filterTenantId) ||
          (!req.tenantId && allowedEmails.includes(req.user))
        );

      setLeaveRequests(requests);
    };
    // const fetchLeaveRequests = async () => {
    //   const snapshot = await getDocs(collection(firestore, "leaves"));
      
    //   const filterTenantID =
    //     userRole === "SuperAdmin"
    //       ? selectedTenantID || sessionStorage.getItem("selectedTenantID")
    //       : tenantID || sessionStorage.getItem("selectedTenantID");

    //   const requests = snapshot.docs
    //     .map((doc) => ({ id: doc.id, ...doc.data() }))
    //     .filter((req) => req.tenantID === filterTenantID); // Filter by tenant

    //   setLeaveRequests(requests);
    // };
    /////////////////////////////////////////////////////////////
    // const fetchLeaveRequests = async () => {
    //   const leaveSnapshot = await getDocs(collection(firestore, "leaves"));
    //   const requests = leaveSnapshot.docs.map((doc) => ({
    //     id: doc.id,
    //     ...doc.data(),
    //   }));
    //   setLeaveRequests(requests);
    // };

    const fetchUserMap = async () => {
      const userSnapshot = await getDocs(collection(firestore, "Users"));
      const userMapping = {};
      userSnapshot.docs.forEach((doc) => {
        const userData = doc.data();
        userMapping[userData.userEmail] = userData.userName;
      });
      setUserMap(userMapping);
    };

    fetchLeaveRequests();
    fetchUserMap();
  }, []);

  const handleApproveOrDecline = async (id, status, request) => {
    const leaveDocRef = doc(firestore, "leaves", id);
    await updateDoc(leaveDocRef, { status });
    setLeaveRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, status } : req))
    );

    if (status === "Approved") {
      await addLeaveEntriesToTimer(request);
    }
  };

  const addLeaveEntriesToTimer = async (request) => {
    const { startDate, endDate, user: userEmail } = request;
    const userName = userMap[userEmail];
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = differenceInCalendarDays(end, start) + 1;

    for (let i = 0; i < days; i++) {
      const currentDay = addDays(start, i);
      const date = format(currentDay, "dd-MM-yy");
      const documentId = `${userEmail}_${new Date().getTime()}`;

      const timerData = {
        date: date,
        startTime: "9:00 AM",
        endTime: "5:00 PM",
        totalTimeBooked: "480 mins", // 8 hours
        userEmail: userEmail,
        userName: userName,
        taskList: "Leave",
      };

      await setDoc(doc(firestore, "Timer", documentId), timerData);
    }
  };

  const toggleShowAllRequests = () => {
    setShowAllRequests((prev) => !prev);
  };

  const pendingRequests = leaveRequests.filter((request) => request.status === "Pending");
  const processedRequests = leaveRequests.filter((request) => request.status !== "Pending");

  return (
    <>
      <PageNavBar title="Leave Approval" backTo="/dashboard" />
      <TenantBanner />
      <Container maxWidth="md">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" align="center" gutterBottom>
            Leave Approvals
          </Typography>

          <Table sx={{ mt: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Leave Type</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{userMap[request.user] || "N/A"}</TableCell>
                  <TableCell>{request.leaveType}</TableCell>
                  <TableCell>{request.startDate}</TableCell>
                  <TableCell>{request.endDate}</TableCell>
                  <TableCell>{request.remarks}</TableCell>
                  <TableCell>{request.status}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleApproveOrDecline(request.id, "Approved", request)}
                      sx={{ mr: 1 }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleApproveOrDecline(request.id, "Declined", request)}
                    >
                      Decline
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button variant="contained" onClick={toggleShowAllRequests}>
              {showAllRequests ? "Hide Data" : "View Data"}
            </Button>
          </Box>

          {showAllRequests && (
            <Table sx={{ mt: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Remarks</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaveRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{userMap[request.user] || "N/A"}</TableCell>
                    <TableCell>{request.leaveType}</TableCell>
                    <TableCell>{request.startDate}</TableCell>
                    <TableCell>{request.endDate}</TableCell>
                    <TableCell>{request.remarks}</TableCell>
                    <TableCell>{request.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Container>
    </>
  );
};
  
export default ApproveLeave;
  